import { pulseColors } from '@/solaris-lens-common-lib/src/styles/colors';

/**
 * Injects all colors from pulseColors as CSS variables
 * This allows using JavaScript colors in CSS
 *
 * @example
 * ```tsx
 * // Call once at app initialization
 * injectColorsToCSS();
 *
 * // Then use in CSS:
 * // .myComponent { color: var(--color-dark-blue); }
 * ```
 */
export const injectColorsToCSS = () => {
  const root = document.documentElement;

  // Convert pulseColors to CSS variables
  Object.entries(pulseColors).forEach(([key, value]) => {
    const cssVariableName = `--color-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
    root.style.setProperty(cssVariableName, value);
  });
};

/**
 * Gets a specific color value from pulseColors
 *
 * @param colorName - Name of the color (e.g., 'darkBlue')
 * @returns The color value as a string
 *
 * @example
 * ```tsx
 * const darkBlue = getColor('darkBlue'); // '#1C496D'
 * ```
 */
export const getColor = (colorName: keyof typeof pulseColors): string => {
  return pulseColors[colorName];
};

/**
 * Applies a specific color as a CSS variable
 *
 * @param colorName - Name of the color from pulseColors
 * @param cssVariableName - Custom CSS variable name (optional)
 *
 * @example
 * ```tsx
 * // Apply darkBlue as --my-custom-color
 * applyColorAsCSSVariable('darkBlue', '--my-custom-color');
 * ```
 */
export const applyColorAsCSSVariable = (
  colorName: keyof typeof pulseColors,
  cssVariableName?: string
) => {
  const root = document.documentElement;
  const variableName = cssVariableName || `--color-${colorName}`;
  root.style.setProperty(variableName, pulseColors[colorName]);
};
